import { useAppDispatch } from "@app/hooks"
import { RootState } from "@app/store"
import { ShiftsClient } from "@clients/shiftsClient"
import BackButton from "@cmp/buttons/backButton"
import {
  availableShiftsSelector,
  salesTeamWithShiftsSelector,
  selectConsultant,
  selectConsultantShifts,
} from "@features/sales-management/salesManagement.selectors"
import {
  updateConsultantAvailability,
  fetchShifts,
  editShift,
} from "@features/sales-management/salesManagement.slice"
import { CreateShiftRequest, ShiftDto } from "@masterschool/course-builder-api"
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material"
import { useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import { ConsultantShifts } from "./consultantShifts"
import { CreateOrAttachShift } from "./createOrAttachShift"
import GenericDialog from "@cmp/genericDialog"
import { CreateOrEditShiftForm } from "./createOrEditShiftForm"

// A consultant's shift editor. Everything that used to gate routing per
// consultant (QTF acceptance, languages, "golden leads") is gone: the routing
// rules own that now, so this page is only about availability and shifts.
export const ShiftManagement = () => {
  const { consultantId } = useParams()
  const dispatch = useAppDispatch()
  const selectConsultantShiftsMemo = useMemo(() => {
    return (state: RootState) => selectConsultantShifts(state, consultantId)
  }, [consultantId])
  const selectConsultantMemo = useMemo(() => {
    return (state: RootState) => selectConsultant(state, consultantId)
  }, [consultantId])
  const shifts = useSelector(selectConsultantShiftsMemo)
  const orderedShifts = shifts.toSorted((a, b) => a.name.localeCompare(b.name))
  const consultant = useSelector(selectConsultantMemo)
  const salesTeamsWithShifts = useSelector(salesTeamWithShiftsSelector)
  const availableShifts = useSelector(availableShiftsSelector)
  const filteredAvailableShifts =
    availableShifts !== "pending"
      ? availableShifts.filter(
          (availableShift) =>
            shifts.find((shift) => shift.id === availableShift.id) ===
            undefined,
        )
      : []
  const [shiftToEdit, setShiftToEdit] = useState<ShiftDto | undefined>(
    undefined,
  )
  const [shiftToDelete, setShiftToDelete] = useState<ShiftDto | undefined>(
    undefined,
  )

  const flattenConsultants =
    salesTeamsWithShifts !== "pending"
      ? salesTeamsWithShifts.flatMap((team) => team.consultants)
      : []
  const otherConsultants = flattenConsultants.filter(
    (consultantWithShift) => consultantWithShift.id !== consultantId,
  )

  const otherConsultantsWithSameShift = shiftToEdit
    ? otherConsultants.filter((consultant) =>
        consultant.shifts.find((shift) => shift.id === shiftToEdit.id),
      )
    : []

  const navigate = useNavigate()
  const handleAvailableChange = (params: {
    consultantId: string
    newIsAvailable: boolean
  }) => {
    dispatch(
      updateConsultantAvailability({
        consultantId: params.consultantId,
        isAvailable: params.newIsAvailable,
      }),
    )
  }
  const onDeleteShift = (shiftId: string) => {
    setShiftToDelete(shifts.find((shift) => shift.id === shiftId))
  }

  const confirmDeleteShift = () => {
    if (!shiftToDelete) {
      return
    }
    ShiftsClient.deleteShift(shiftToDelete.id).then(() => {
      setShiftToDelete(undefined)
      dispatch(fetchShifts())
    })
  }

  const consultantsSharingDeletedShift = shiftToDelete
    ? otherConsultants.filter((consultant) =>
        consultant.shifts.find((shift) => shift.id === shiftToDelete.id),
      )
    : []

  const handleEditShiftSubmit = (editedShiftRequest: CreateShiftRequest) => {
    if (!shiftToEdit) {
      return
    }

    const editedShift = {
      id: shiftToEdit.id,
      ...editedShiftRequest,
    }
    dispatch(editShift(editedShift))
    setShiftToEdit(undefined)
  }

  const onEditButtonClicked = (shiftId: string) => {
    const shift = shifts.find((shift) => shift.id === shiftId)
    setShiftToEdit(shift)
  }

  const onAttachShift = (shiftDto: ShiftDto) => {
    if (!consultantId) {
      return
    }
    ShiftsClient.attach(shiftDto.id, consultantId).then(() => {
      dispatch(fetchShifts())
    })
  }

  const onCreateCustomShift = (createShiftRequest: CreateShiftRequest) => {
    if (!consultantId) {
      return
    }

    ShiftsClient.createAndAttach(createShiftRequest, consultantId).then(() => {
      dispatch(fetchShifts())
    })
  }

  if (!consultant) {
    return <Navigate to="/sales-management" />
  }

  return (
    <Box
      display="flex"
      height="100%"
      flexDirection="column"
      paddingX="48px"
      paddingBottom="24px"
    >
      <Box display="flex" flexDirection="column" gap="32px" overflow="scroll">
        <Box paddingTop="32px">
          <BackButton
            onClick={() => {
              navigate("/sales-management")
            }}
          />
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          width="100%"
          paddingTop="32px"
        >
          <Typography variant="h4" textAlign="left" textTransform="capitalize">
            {consultant.name}
          </Typography>
          <Tooltip title="Manual pause on top of the schedule. A consultant gets leads only when this is on and they're on a shift right now.">
            <Box display="flex" alignItems="center">
              <Switch
                checked={consultant.isAvailable}
                onChange={(e) =>
                  handleAvailableChange({
                    consultantId: consultant.id,
                    newIsAvailable: e.target.checked,
                  })
                }
              />
              <Typography variant="body1" color="textSecondary">
                Accepting leads
              </Typography>
            </Box>
          </Tooltip>
        </Box>
        {!consultant.isAvailable && (
          <Alert severity="error">
            {consultant.name} is paused, so they won't receive any new leads
            even while on shift
          </Alert>
        )}
        <Box display="flex" flexDirection="row" gap="24px" width="100%">
          <ConsultantShifts
            shifts={orderedShifts}
            onDeleteShift={onDeleteShift}
            onEditShift={(shiftId: string) => onEditButtonClicked(shiftId)}
          />
          <CreateOrAttachShift
            availableShifts={filteredAvailableShifts}
            onAttachShift={onAttachShift}
            onCreateShift={onCreateCustomShift}
          />
        </Box>
      </Box>
      <GenericDialog
        open={shiftToEdit !== undefined}
        size="sm"
        onClose={() => setShiftToEdit(undefined)}
        title={`Edit shift`}
        content={
          <Box paddingTop="16px" display="flex" flexDirection="column">
            <CreateOrEditShiftForm
              onSubmit={handleEditShiftSubmit}
              initialShift={shiftToEdit}
            />
            {otherConsultantsWithSameShift.length > 0 && (
              <Typography variant="body2" textAlign="center" paddingTop="16px">
                Pay attention, this Shift is also being used by other
                consultants:
                {otherConsultantsWithSameShift.map(
                  (c, index) =>
                    " " +
                    c.name +
                    (index === otherConsultantsWithSameShift.length - 1
                      ? "."
                      : ","),
                )}
              </Typography>
            )}
          </Box>
        }
        buttons={[]}
      />
      <Dialog
        open={shiftToDelete !== undefined}
        onClose={() => setShiftToDelete(undefined)}
        maxWidth="xs"
      >
        <DialogTitle>Delete “{shiftToDelete?.name}”?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {consultantsSharingDeletedShift.length > 0
              ? `This shift is also used by ${consultantsSharingDeletedShift
                  .map((c) => c.name)
                  .join(", ")}. Deleting it removes it for everyone.`
              : "This deletes the shift. It can't be undone."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShiftToDelete(undefined)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDeleteShift}
          >
            Delete shift
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
